import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './axiosConfig';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid, Alert,
  CircularProgress, Box, Typography
} from '@mui/material';

// Sickness absence confirmation letter. Pre-filled from the absence record and the employee's
// working pattern; the manager checks it and creates the letter, which is saved to the Document Vault.
//   <SickLetterDialog open={!!id} absenceId={id} onClose={() => setId(null)} />
export default function SickLetterDialog({ open, absenceId, onClose }) {
  const navigate = useNavigate();
  const [prefill, setPrefill] = useState(null);
  const [fields, setFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!open || !absenceId) return;
    let cancelled = false;
    setLoading(true); setError(''); setDone(null); setPrefill(null);
    api.get(`/letter-gen/prefill/sickness-confirmation/${absenceId}`)
      .then((res) => {
        if (cancelled) return;
        const { warnings, employee_name, ...editable } = res.data.prefill;
        setPrefill(res.data.prefill);
        setFields(editable);
      })
      .catch((e) => !cancelled && setError(e.response?.data?.error || 'Could not load this absence.'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, absenceId]);

  const set = (name) => (e) => setFields((f) => ({ ...f, [name]: e.target.value }));

  const handleGenerate = async () => {
    setSaving(true); setError('');
    try {
      const res = await api.post(`/letter-gen/generate/sickness-confirmation/${absenceId}`, fields);
      setDone(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not create the letter.');
    } finally {
      setSaving(false);
    }
  };

  const text = (name, label, extra = {}) => (
    <TextField fullWidth size="small" label={label} value={fields[name] ?? ''} onChange={set(name)} {...extra} />
  );
  const date = (name, label, extra = {}) => text(name, label, { type: 'date', InputLabelProps: { shrink: true }, ...extra });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Sickness absence letter{prefill ? ` - ${prefill.employee_name}` : ''}</DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ textAlign: 'center', p: 3 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {done ? (
          <Alert severity="success">
            {done.message}.{' '}
            <Button size="small" onClick={() => { onClose(); navigate('/documents'); }}>Open Document Vault</Button>
          </Alert>
        ) : prefill && (
          <>
            {prefill.warnings.map((w) => <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Check the dates and wording. The number of calendar days and of SSP qualifying days (the days the
              employee normally works) are worked out from these dates when the letter is created.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>{date('absence_start_date', 'First day of absence')}</Grid>
              <Grid item xs={12} sm={6}>{date('absence_end_date', 'Last day of absence')}</Grid>
              <Grid item xs={12}>{text('absence_reason', 'Reason for absence')}</Grid>
              <Grid item xs={12} sm={6}>
                {date('interview_date', 'Return-to-work interview date', {
                  helperText: 'Leave empty if no interview has been held yet.',
                })}
              </Grid>
              <Grid item xs={12} sm={6}>{text('interviewer', 'Interview held by')}</Grid>
              <Grid item xs={12}>
                {text('ssp_details', 'Statutory Sick Pay wording', { multiline: true, minRows: 3 })}
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{done ? 'Close' : 'Cancel'}</Button>
        {!done && (
          <Button variant="contained" onClick={handleGenerate} disabled={saving || loading || !prefill}>
            {saving ? 'Creating...' : 'Create letter'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
